import { motion } from "framer-motion";
import { Construction, Lock, Shirt, Star, ShoppingBag, Monitor, Gamepad2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Countdown from "./Countdown";
import { useCart } from "./Cart";
import { optimizedImage } from "@/lib/image";

export default function Store({ data, countdown }) {
  const { addItem } = useCart();
  return (
    <section
      id="store"
      data-testid="store-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-white/5 overflow-hidden"
    >
      {/* Key art background */}
      {data.backgroundImage && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img
            src={optimizedImage(data.backgroundImage, { width: 1920 })}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
      )}

      {/* Ambient glow */}
      <div className="absolute -top-32 left-1/3 w-[600px] h-[600px] rounded-full bg-accent/[0.06] blur-3xl pointer-events-none" />

      <div className="max-w-[1600px] mx-auto relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14 md:mb-20"
        >
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                {data.eyebrow}
              </span>
            </div>
            <h2
              data-testid="store-title"
              className="font-heading uppercase text-white text-6xl md:text-7xl lg:text-8xl leading-[0.9]"
            >
              {data.title}
            </h2>
          </div>
          <p className="lg:col-span-4 font-body text-base md:text-lg text-white/60 leading-relaxed max-w-md">
            {data.description}
          </p>
        </motion.div>

        {/* CTA to full store page */}
        {data.ctaVisit && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-10 md:mb-14 flex justify-start"
          >
            <Link
              to="/store"
              data-testid="visit-store-cta"
              className="group inline-flex items-center gap-4 bg-white text-black px-6 py-4 hover:bg-accent transition-colors duration-300"
            >
              <span className="font-heading text-lg tracking-[0.15em] uppercase">
                {data.ctaVisit}
              </span>
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5"
              />
            </Link>
          </motion.div>
        )}

        {/* Notice banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="store-notice"
          className="relative border-y-2 border-accent bg-accent/[0.07] py-10 md:py-14 px-6 md:px-12 mb-14 md:mb-16 overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #FACC15 0, #FACC15 12px, transparent 12px, transparent 32px)",
            }}
          />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex items-center gap-4 shrink-0">
              <span className="relative flex items-center justify-center w-14 h-14 border border-accent/60 bg-black/60">
                <Construction size={22} className="text-accent" />
                <span className="absolute inset-0 border border-accent/40 animate-pulse" />
              </span>
              <div className="flex flex-col gap-1">
                <span className="font-body text-[10px] uppercase tracking-[0.35em] text-accent">
                  Avviso
                </span>
                <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/60">
                  Sistema — in sviluppo
                </span>
              </div>
            </div>
            <div className="w-px h-16 bg-accent/30 hidden md:block" />
            <div className="flex-1">
              <div
                data-testid="store-notice-headline"
                className="font-heading uppercase text-white text-3xl md:text-5xl lg:text-6xl leading-[0.95] mb-3"
              >
                {data.notice}
              </div>
              <p className="font-body text-sm md:text-base text-white/70 max-w-2xl leading-relaxed">
                {data.noticeDetail}
              </p>
              <div
                data-testid="store-opening"
                className="mt-6 inline-flex items-center gap-4 border border-accent/60 bg-black/50 backdrop-blur-sm px-5 py-3"
              >
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="font-heading text-lg md:text-2xl uppercase tracking-[0.12em] text-accent">
                  {data.opening}
                </span>
                <span className="hidden md:inline w-px h-6 bg-accent/40" />
                <span className="hidden md:inline font-body text-[10px] uppercase tracking-[0.3em] text-white/60">
                  {data.openingSubtitle}
                </span>
              </div>
              <p className="md:hidden font-body text-[10px] uppercase tracking-[0.3em] text-white/50 mt-3">
                {data.openingSubtitle}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Preorder countdown (game editions) */}
        {countdown && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-14 md:mb-16 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 border border-accent/30 bg-accent/[0.03] p-6 md:p-8"
            data-testid="store-countdown-block"
          >
            <div className="shrink-0">
              <div className="font-body text-[10px] uppercase tracking-[0.35em] text-accent mb-2">
                {countdown.label}
              </div>
              <div className="font-heading uppercase text-white text-3xl md:text-4xl leading-none">
                {countdown.date}
              </div>
            </div>
            <div className="md:ml-auto">
              <Countdown targetDate={countdown.targetDate} testId="store-countdown" />
            </div>
          </motion.div>
        )}

        {/* Release plan */}
        {data.releasePlan && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            data-testid="release-plan"
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border border-white/10 bg-[#09090B] p-8 md:p-12 mb-14 md:mb-16"
          >
            <div className="md:col-span-4 flex items-start gap-6">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-12 h-12 border border-white/15">
                  <Monitor size={18} className="text-accent" />
                </span>
                <span className="font-heading text-2xl text-white/20">→</span>
                <span className="flex items-center justify-center w-12 h-12 border border-white/15">
                  <Gamepad2 size={18} className="text-white/60" />
                </span>
              </div>
              <div>
                <div className="font-body text-[10px] uppercase tracking-[0.35em] text-accent mb-2">
                  {data.releasePlan.label}
                </div>
                <div className="font-heading uppercase text-white text-2xl md:text-3xl leading-tight">
                  {data.releasePlan.headline}
                </div>
              </div>
            </div>
            <p className="md:col-span-8 font-body text-base md:text-lg text-white/65 leading-relaxed self-center">
              {data.releasePlan.body}
            </p>
          </motion.div>
        )}

        {/* Store tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {data.placeholderItems.map((item, i) => {
            const isCollector = !!item.featured;
            const hasImage = !!item.image;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`store-item-${item.id}`}
                className={`relative group border flex flex-col overflow-hidden transition-colors duration-500 ${
                  isCollector
                    ? "border-accent/40 bg-[#1A1A1F] hover:border-accent"
                    : "border-white/10 bg-[#09090B] hover:border-white/25"
                }`}
              >
                {/* Image / decorative area */}
                <div className={`relative overflow-hidden ${isCollector ? "aspect-[4/3]" : "aspect-[4/3]"}`}>
                  {hasImage ? (
                    <>
                      <img
                        src={optimizedImage(item.image, { width: 900, height: 675 })}
                        alt={item.label}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
                      {isCollector && (
                        <>
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(250,204,21,0.12),transparent_60%)]" />
                          <div className="absolute top-5 left-5 flex items-center gap-2 border border-accent/50 bg-black/60 backdrop-blur-sm px-3 py-1.5">
                            <Star size={11} className="text-accent" />
                            <span className="font-body text-[9px] uppercase tracking-[0.3em] text-accent">
                              Edizione Limitata
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-white/[0.05] font-heading uppercase text-[9rem] leading-none select-none">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                      </div>
                      <div
                        className="absolute inset-0 opacity-[0.04] pointer-events-none"
                        style={{
                          backgroundImage:
                            "repeating-linear-gradient(135deg, #ffffff 0, #ffffff 1px, transparent 1px, transparent 12px)",
                        }}
                      />
                    </>
                  )}
                  <div className="absolute top-5 right-5 flex items-center gap-2 border border-white/15 bg-black/60 backdrop-blur-sm px-3 py-1.5">
                    <Lock size={11} className="text-accent" />
                    <span className="font-body text-[9px] uppercase tracking-[0.3em] text-white/80">
                      {item.status}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className={`relative p-6 md:p-8 border-t backdrop-blur-sm flex-1 flex flex-col ${isCollector ? "border-accent/20 bg-black/70" : "border-white/10 bg-black/30"}`}>
                  <div className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                    The Protocol Lite
                  </div>
                  <div className={`font-heading uppercase leading-none transition-colors duration-500 ${isCollector ? "text-white text-3xl md:text-4xl group-hover:text-accent" : "text-white text-2xl md:text-3xl group-hover:text-accent"}`}>
                    {item.label}
                  </div>

                  {item.includes && (
                    <ul className="mt-6 space-y-2">
                      {item.includes.map((inc) => (
                        <li key={inc} className="flex items-start gap-3 font-body text-sm text-white/75 leading-snug">
                          <span className="mt-1.5 w-1 h-1 bg-accent shrink-0" />
                          {inc}
                        </li>
                      ))}
                    </ul>
                  )}

                  {item.apparel && (
                    <div className="mt-5 border-t border-white/10 pt-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Shirt size={14} className="text-accent" />
                        <span className="font-body text-[10px] uppercase tracking-[0.3em] text-accent">
                          {item.apparel.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {item.apparel.options.map((opt) => (
                          <span
                            key={opt}
                            className="font-body text-[11px] uppercase tracking-[0.2em] text-white/85 border border-white/20 bg-white/[0.03] px-3 py-1.5"
                          >
                            {opt}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preorder area */}
                  <div className="mt-auto pt-6">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                        <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/60">
                          {item.preorderNote}
                        </span>
                      </div>
                      {item.price && (
                        <span
                          data-testid={`store-item-price-${item.id}`}
                          className="font-heading text-2xl md:text-3xl leading-none text-white select-none"
                        >
                          {item.price}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid={`preorder-btn-${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        addItem({
                          id: item.id,
                          name: `${item.label} · The Protocol Lite`,
                          price: item.price,
                          image: item.image,
                          variant: item.apparel?.options?.[0] || null,
                        });
                      }}
                      className="w-full inline-flex items-center justify-center gap-3 border border-accent/40 bg-accent/[0.06] backdrop-blur-sm px-5 py-3.5 cursor-pointer hover:bg-accent/[0.15] hover:border-accent transition-colors"
                    >
                      <ShoppingBag size={13} className="text-accent" />
                      <span className="font-heading text-base tracking-[0.15em] uppercase text-accent">
                        Aggiungi al carrello
                      </span>
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
